import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
// NgSelectModule (not the bare component) carries the ng-option-tmp /
// ng-label-tmp directives the SKU cell's two-line rows need.
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrService } from 'ngx-toastr';
import { Subject, catchError, debounceTime, of, switchMap } from 'rxjs';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { AuthService } from '../../../auth/auth.service';
import { FormDraftStore } from '../../../shared/utils/form-draft-store';
import { ProductsService } from '../products.service';
import {
  BarcodeRowError,
  BulkBarcodeRow,
  SkuBarcodeLookup,
  VariantBarcodeService,
} from '../variant-barcode.service';

/** `sku` = searchable product dropdown (name + SKU), `auto` = read-only. */
type ColType = 'sku' | 'text' | 'bool' | 'auto';

export interface ColumnDef {
  key: string;
  label: string;
  type: ColType;
  required?: boolean;
  width: number;
  placeholder?: string;
  maxLength?: number;
  hint?: string;
}

interface GridRow {
  uid: number;
  values: Record<string, any>;
  errors: Record<string, string>;
  warnings: Record<string, string>;
  /** Backend knowledge about the typed SKU (name, current EANs, price state). */
  lookup: SkuBarcodeLookup | null;
  lookingUp: boolean;
  /** Dropdown options of this row's SKU cell (server-searched by name or SKU). */
  skuItems: SkuOption[];
  skuLoading: boolean;
}

/** One product in the SKU cell's dropdown. */
interface SkuOption {
  sku: string;
  longDescription?: string | null;
  id?: string;
}

/** The confirm dialog renders its message as HTML. */
function escapeHtml(text: string): string {
  return String(text).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/**
 * What the browser safety copy of the unsaved grid holds (see autosaveDraft):
 * each row's typed values (sku, code, makePrimary, retireCode), in grid order.
 * SKU lookups are re-run on restore.
 */
interface BulkEanDraftSnapshot {
  rows: Array<Record<string, any>>;
}

/** Width of the row-number gutter; must match .row-num in the shared stylesheet. */
const ROW_NUM_WIDTH = 44;
/** Width of the trailing actions column; must match .row-actions. */
const ROW_ACTIONS_WIDTH = 76;
const FROZEN_COLUMN_KEYS = ['sku', 'name'];
const INITIAL_ROWS = 10;
const MAX_ROWS = 2000;
const BOOL_OPTIONS = [
  { id: true, name: 'Yes' },
  { id: false, name: 'No' },
];

/**
 * Bulk EAN Mapping — tag or replace barcodes on many SKUs at once.
 *
 * Barcodes are IDENTITY, prices are a separate list: this grid never touches
 * pricing. Type (or paste) a SKU and the new EAN; the grid looks the SKU up,
 * shows its name and the EANs it already carries, and on save tags every row
 * together — all or nothing, exactly like Bulk Add Products. "Retire EAN"
 * deactivates an existing code in the same save (the "update EAN" case).
 */
@Component({
  selector: 'app-bulk-ean-mapping',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgSelectModule],
  templateUrl: './bulk-ean-mapping.component.html',
  // Same look as the products grid: one stylesheet, one set of class names.
  styleUrls: ['../bulk-add-products/bulk-add-products.component.scss'],
})
export class BulkEanMappingComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly barcodes = inject(VariantBarcodeService);
  private readonly productService = inject(ProductsService);
  private readonly toastr = inject(ToastrService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  /** On-screen column order. */
  readonly columns: ColumnDef[] = [
    {
      key: 'sku',
      label: 'SKU',
      type: 'sku',
      required: true,
      width: 260,
      placeholder: 'Search name or SKU',
      hint: 'Type part of the product name or the SKU code, or scan it',
    },
    {
      key: 'name',
      label: 'Product',
      type: 'auto',
      width: 240,
      hint: 'Filled from the SKU',
    },
    {
      key: 'existing',
      label: 'Existing EANs',
      type: 'auto',
      width: 220,
      hint: 'Active barcodes already on the SKU (★ = primary)',
    },
    {
      key: 'code',
      label: 'New EAN',
      type: 'text',
      required: true,
      width: 170,
      maxLength: 64,
      placeholder: 'EAN / barcode',
    },
    {
      key: 'makePrimary',
      label: 'Make primary',
      type: 'bool',
      width: 120,
      hint: 'Primary = the one code shown on lists, labels, reports and the GRN prefill. Every active barcode scans regardless. Defaults to Yes: the newest code becomes the displayed one.',
    },
    {
      key: 'retireCode',
      label: 'Retire EAN',
      type: 'text',
      width: 170,
      maxLength: 64,
      placeholder: 'optional',
      hint: 'An existing barcode of this SKU to deactivate in the same save',
    },
  ];
  readonly boolOptions = BOOL_OPTIONS;
  frozenLeft: Record<string, number | undefined> = {};
  lastFrozenKey: string | null = null;
  tableWidth = 0;

  rows: GridRow[] = [];
  private nextUid = 1;
  activeCell: { row: number; col: string } | null = null;

  saving = false;
  validating = false;
  submitted = false;
  errorList: BarcodeRowError[] = [];
  warningList: BarcodeRowError[] = [];
  lastResult: { created: number; reactivated: number; retired: number; unchanged: number } | null = null;

  /** SKU cells settle for a moment before the grid asks the backend about them. */
  private readonly lookupQueue = new Subject<void>();
  /** Keystrokes in a SKU cell: debounced, latest term wins, one row at a time. */
  private readonly skuSearch$ = new Subject<{ term: string; row: GridRow }>();
  /** Server already filtered by name/SKU — never let ng-select re-filter by label. */
  readonly matchAll = (): boolean => true;

  get filledRowCount(): number {
    return this.rows.filter((r) => !this.isRowEmpty(r)).length;
  }

  ngOnInit(): void {
    this.rebuildLayout();
    this.rows = [];
    this.addRows(INITIAL_ROWS);
    this.lookupQueue.pipe(debounceTime(350)).subscribe(() => this.runLookups());
    this.skuSearch$
      .pipe(
        debounceTime(250),
        switchMap(({ term, row }) => {
          const q = term.trim();
          if (!q) {
            row.skuLoading = false;
            return of({ row, items: [] as SkuOption[] });
          }
          row.skuLoading = true;
          return this.productService
            .searchProducts({ query: q, limit: 20 })
            .pipe(
              catchError(() => of({ data: [] as any[], total: 0 })),
              switchMap((res) =>
                of({
                  row,
                  items: (res.data ?? []).map((p: any) => ({
                    id: p.id,
                    sku: p.sku,
                    longDescription: p.longDescription || p.shortDescription || p.name,
                  })),
                }),
              ),
            );
        }),
      )
      .subscribe(({ row, items }) => {
        row.skuLoading = false;
        row.skuItems = items;
        this.cdr.markForCheck();
      });
    // No master data to wait for: restored SKUs are looked up again.
    this.initDraft();
    setTimeout(() => this.offerDraftRestore());
  }

  ngOnDestroy(): void {
    if (this.draftTimer) clearInterval(this.draftTimer);
    this.autosaveDraft();
  }

  // ───────────────────────── SKU cell (searchable dropdown) ─────────────────────────

  onSkuSearch(event: { term?: string }, row: GridRow): void {
    this.skuSearch$.next({ term: event?.term ?? '', row });
  }

  /** ng-select `(change)`: the chosen product, or undefined when cleared. */
  onSkuSelected(product: SkuOption | string | undefined, row: GridRow): void {
    row.values['sku'] =
      typeof product === 'string' ? product : (product?.sku ?? null);
    this.onCellChange(row, 'sku');
  }

  /**
   * ng-select shows a value that is not in its item list as the bare code.
   * Once the lookup resolves (or a code arrives by paste), make sure the
   * row's list carries a matching option so the cell reads "SKU · name".
   */
  private ensureSkuOption(row: GridRow): void {
    const sku = String(row.values['sku'] ?? '').trim();
    if (!sku) return;
    if (row.skuItems.some((o) => o.sku === sku)) return;
    row.skuItems = [
      { sku, longDescription: row.lookup?.name ?? null, id: row.lookup?.productId },
      ...row.skuItems,
    ];
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.rebuildLayout());
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.rebuildLayout();
  }

  // ───────────────────────── layout ─────────────────────────

  private rebuildLayout(): void {
    let left = ROW_NUM_WIDTH;
    this.frozenLeft = {};
    this.lastFrozenKey = null;
    for (const col of this.columns) {
      if (!FROZEN_COLUMN_KEYS.includes(col.key)) break;
      this.frozenLeft[col.key] = left;
      this.lastFrozenKey = col.key;
      left += col.width;
    }
    this.tableWidth =
      ROW_NUM_WIDTH +
      this.columns.reduce((sum, c) => sum + c.width, 0) +
      ROW_ACTIONS_WIDTH;
    this.cdr.markForCheck();
  }

  // ───────────────────────── rows ─────────────────────────

  private newRow(): GridRow {
    return {
      uid: this.nextUid++,
      values: { makePrimary: true },
      errors: {},
      warnings: {},
      lookup: null,
      lookingUp: false,
      skuItems: [],
      skuLoading: false,
    };
  }

  addRows(count: number): void {
    const room = MAX_ROWS - this.rows.length;
    if (room <= 0) {
      this.toastr.warning(`The grid holds at most ${MAX_ROWS} rows per save.`);
      return;
    }
    for (let i = 0; i < Math.min(count, room); i++) this.rows.push(this.newRow());
  }

  removeRow(index: number): void {
    this.rows.splice(index, 1);
    if (!this.rows.length) this.addRows(1);
    this.rebuildErrorList();
  }

  /** Copy the SKU below itself (same product, another barcode). */
  duplicateRow(index: number): void {
    if (this.rows.length >= MAX_ROWS) {
      this.toastr.warning(`The grid holds at most ${MAX_ROWS} rows per save.`);
      return;
    }
    const src = this.rows[index];
    const copy = this.newRow();
    copy.values = { sku: src.values['sku'], makePrimary: false };
    copy.lookup = src.lookup;
    copy.skuItems = [...src.skuItems];
    this.rows.splice(index + 1, 0, copy);
  }

  async clearGrid(): Promise<void> {
    if (this.filledRowCount > 0) {
      const ok = await this.confirmDialog.confirm({
        title: 'Clear grid?',
        message: `Discard the ${this.filledRowCount} row(s) you have filled in?`,
        confirmText: 'Clear',
        cancelText: 'Keep',
      });
      if (!ok) return;
      // Discarded on purpose: the browser copy goes too. Not closed, so the
      // next rows typed here are still protected.
      this.draftStore?.clear();
    }
    this.rows = [];
    this.addRows(INITIAL_ROWS);
    this.errorList = [];
    this.warningList = [];
    this.lastResult = null;
  }

  isRowEmpty(row: GridRow): boolean {
    return this.isBlankValues(row.values);
  }

  private isBlankValues(values: Record<string, any> | null | undefined): boolean {
    return !['sku', 'code', 'retireCode'].some((k) => {
      const v = values?.[k];
      return v !== undefined && v !== null && String(v).trim() !== '';
    });
  }

  // ───────────────────────── cell editing ─────────────────────────

  setActive(rowIndex: number, colKey: string): void {
    this.activeCell = { row: rowIndex, col: colKey };
  }

  onCellChange(row: GridRow, colKey: string): void {
    delete row.errors[colKey];
    delete row.warnings[colKey];
    this.submitted = false;
    if (colKey === 'sku') {
      if (typeof row.values['sku'] === 'string')
        row.values['sku'] = row.values['sku'].trim().toUpperCase();
      // The SKU changed: what we knew about the old one no longer applies.
      row.lookup = null;
      this.ensureSkuOption(row);
      this.lookupQueue.next();
    }
    if (this.errorList.length) this.rebuildErrorList();
  }

  /** Resolve every SKU the grid does not know yet, in one request. */
  private runLookups(): void {
    const pending = this.rows.filter(
      (r) => !r.lookup && String(r.values['sku'] ?? '').trim() && !r.lookingUp,
    );
    if (!pending.length) return;
    const skus = Array.from(new Set(pending.map((r) => String(r.values['sku']).trim())));
    pending.forEach((r) => (r.lookingUp = true));
    this.barcodes.bulkLookup(skus).subscribe({
      next: (found) => {
        const bySku = new Map(found.map((f) => [f.sku.toUpperCase(), f]));
        pending.forEach((r) => {
          r.lookingUp = false;
          const key = String(r.values['sku'] ?? '').trim().toUpperCase();
          const hit = bySku.get(key) ?? found.find((f) => f.sku.toUpperCase() === key) ?? null;
          r.lookup = hit ?? { sku: key, found: false, barcodes: [] };
          if (!r.lookup.found) r.errors['sku'] = `SKU ${key} not found`;
          else {
            delete r.errors['sku'];
            // Let the closed SKU cell show "SKU · name" for a pasted code.
            r.skuItems = r.skuItems.map((o) =>
              o.sku === r.lookup!.sku && !o.longDescription
                ? { ...o, longDescription: r.lookup!.name ?? null, id: r.lookup!.productId }
                : o,
            );
            // The newest tagged code is the displayed one unless the user
            // says otherwise (scanning works for every barcode regardless).
            if (r.values['makePrimary'] === null || r.values['makePrimary'] === undefined)
              r.values['makePrimary'] = true;
          }
        });
        this.rebuildErrorList();
        this.cdr.markForCheck();
      },
      error: () => {
        pending.forEach((r) => (r.lookingUp = false));
        this.toastr.error('Could not look up SKUs. Try again.');
      },
    });
  }

  existingLabel(row: GridRow): string {
    if (!row.lookup?.found) return '';
    return row.lookup.barcodes
      .map((b) => (b.isPrimary ? `★ ${b.code}` : b.code))
      .join(', ');
  }

  /** Copy the active cell (or row 1) of this column into every row below it. */
  fillDown(col: ColumnDef): void {
    const from = this.activeCell?.col === col.key ? this.activeCell.row : 0;
    const source = this.rows[from];
    if (!source) return;
    const value = source.values[col.key];
    for (let i = from + 1; i < this.rows.length; i++) {
      this.rows[i].values[col.key] = value;
      this.onCellChange(this.rows[i], col.key);
    }
    this.toastr.info(`${col.label} copied down from row ${from + 1}.`);
  }

  /** Enter moves to the same column on the next row, adding one at the end. */
  moveDown(event: Event, rowIndex: number, colKey: string): void {
    event.preventDefault();
    if (rowIndex === this.rows.length - 1) this.addRows(1);
    setTimeout(() => this.focusCell(rowIndex + 2, colKey, false));
  }

  /**
   * Spreadsheet paste: a tab/newline block fills across the editable columns
   * and down the rows from the pasted cell. Read-only columns are skipped so
   * a two-column "SKU <tab> EAN" sheet lands in SKU and New EAN.
   */
  onPaste(event: ClipboardEvent, rowIndex: number, colKey: string): void {
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!text.includes('\n') && !text.includes('\t')) return;
    event.preventDefault();

    const lines = text.replace(/\r/g, '').split('\n');
    if (lines.length && lines[lines.length - 1] === '') lines.pop();
    const editable = this.columns.filter((c) => c.type !== 'auto');
    const startCol = editable.findIndex((c) => c.key === colKey);
    if (startCol < 0) return;

    let pastedCells = 0;
    lines.forEach((line, li) => {
      const targetIndex = rowIndex + li;
      if (targetIndex >= MAX_ROWS) return;
      while (targetIndex >= this.rows.length) this.rows.push(this.newRow());
      const row = this.rows[targetIndex];
      line.split('\t').forEach((cell, ci) => {
        const col = editable[startCol + ci];
        if (!col) return;
        const raw = cell.trim();
        row.values[col.key] =
          col.type === 'bool'
            ? raw === '' ? row.values[col.key] : /^(y|yes|true|1)$/i.test(raw)
            : raw === '' ? null : raw;
        this.onCellChange(row, col.key);
        pastedCells++;
      });
    });
    this.toastr.info(`Pasted ${pastedCells} cell(s) across ${lines.length} row(s).`);
  }

  // ───────────────────────── validation & save ─────────────────────────

  private setError(row: GridRow, col: string, message: string): void {
    if (!row.errors[col]) row.errors[col] = message;
  }

  private validateLocally(rows: GridRow[]): boolean {
    let ok = true;
    const codes = new Map<string, number>();
    rows.forEach((row) => {
      row.errors = {};
      const v = row.values;
      const sku = String(v['sku'] ?? '').trim();
      const code = String(v['code'] ?? '').trim();
      const retire = String(v['retireCode'] ?? '').trim();
      if (!sku) this.setError(row, 'sku', 'SKU is required');
      else if (row.lookup && !row.lookup.found) this.setError(row, 'sku', `SKU ${sku} not found`);
      if (!code) this.setError(row, 'code', 'New EAN is required');
      else {
        if (/\s/.test(code)) this.setError(row, 'code', 'EAN must not contain spaces');
        const n = this.rows.indexOf(row) + 1;
        if (codes.has(code))
          this.setError(row, 'code', `Duplicate EAN in this grid (also row ${codes.get(code)})`);
        else codes.set(code, n);
      }
      if (retire && retire === code)
        this.setError(row, 'retireCode', 'Retire EAN and New EAN are the same');
      if (Object.keys(row.errors).length) ok = false;
    });
    return ok;
  }

  private toPayload(row: GridRow, rowNumber: number): BulkBarcodeRow {
    const v = row.values;
    const out: BulkBarcodeRow = {
      rowNumber,
      sku: String(v['sku'] ?? '').trim(),
      code: String(v['code'] ?? '').trim(),
    };
    if (row.lookup?.variantId) out.variantId = row.lookup.variantId;
    if (v['makePrimary'] !== null && v['makePrimary'] !== undefined)
      out.makePrimary = !!v['makePrimary'];
    const retire = String(v['retireCode'] ?? '').trim();
    if (retire) out.retireCode = retire;
    return out;
  }

  private applyServerErrors(errors: BarcodeRowError[], warnings: BarcodeRowError[]): void {
    errors.forEach((e) => {
      const row = this.rows[e.row - 1];
      if (row) this.setError(row, e.column, e.error);
    });
    warnings.forEach((w) => {
      const row = this.rows[w.row - 1];
      if (row) row.warnings[w.column] = w.error;
    });
    this.rebuildErrorList();
  }

  private rebuildErrorList(): void {
    const list: BarcodeRowError[] = [];
    const warns: BarcodeRowError[] = [];
    this.rows.forEach((row, i) => {
      Object.entries(row.errors).forEach(([column, error]) => list.push({ row: i + 1, column, error }));
      Object.entries(row.warnings).forEach(([column, error]) => warns.push({ row: i + 1, column, error }));
    });
    this.errorList = list;
    this.warningList = warns;
  }

  columnLabel(key: string): string {
    return this.columns.find((c) => c.key === key)?.label ?? key;
  }

  focusCell(rowNumber: number, colKey: string, scroll = true): void {
    const sel = `[data-cell="${rowNumber - 1}:${colKey}"]`;
    const el = this.host.nativeElement.querySelector(`${sel} input, ${sel} select`) as HTMLElement | null;
    if (!el) return;
    el.focus();
    if (scroll) el.scrollIntoView({ block: 'center', inline: 'center' });
  }

  async save(): Promise<void> {
    if (this.saving || this.validating) return;
    const filled = this.rows.filter((r) => !this.isRowEmpty(r));
    this.rows.forEach((r) => {
      r.errors = {};
      r.warnings = {};
    });
    this.lastResult = null;
    if (!filled.length) {
      this.toastr.warning('Fill in at least one row before saving.');
      return;
    }
    if (!this.validateLocally(filled)) {
      this.rebuildErrorList();
      this.toastr.error(`${new Set(this.errorList.map((e) => e.row)).size} row(s) need fixing.`);
      return;
    }

    const payload = filled.map((row) => this.toPayload(row, this.rows.indexOf(row) + 1));
    this.validating = true;
    this.barcodes.bulkValidate(payload).subscribe({
      next: async (res) => {
        this.validating = false;
        this.applyServerErrors(res.errors, res.warnings);
        if (res.errors.length) {
          this.toastr.error(
            `${new Set(res.errors.map((e) => e.row)).size} of ${res.totalRows} row(s) need fixing. Nothing was saved.`,
          );
          return;
        }
        const retiring = payload.filter((p) => p.retireCode).length;
        const warnNote = res.warnings.length
          ? `<br><small class="text-warning-emphasis">${res.warnings.length} warning(s) — see the list under the grid. The rows will still be saved.</small>`
          : '';
        const ok = await this.confirmDialog.confirm({
          title: 'Save barcodes?',
          message: `Tag <strong>${filled.length}</strong> EAN(s)${
            retiring ? ` and retire <strong>${retiring}</strong>` : ''
          }? This saves all rows together — nothing changes if any row fails. Pricing is not affected.${warnNote}`,
          confirmText: `Save ${filled.length}`,
          cancelText: 'Back to grid',
        });
        if (!ok) return;
        this.commit(payload);
      },
      error: (err) => {
        this.validating = false;
        this.handleHttpError(err, 'Validation failed');
      },
    });
  }

  private commit(payload: BulkBarcodeRow[]): void {
    this.saving = true;
    this.barcodes.bulkCommit(payload).subscribe({
      next: (res) => {
        this.saving = false;
        this.submitted = true;
        this.lastResult = {
          created: res.created,
          reactivated: res.reactivated,
          retired: res.retired,
          unchanged: res.unchanged,
        };
        this.toastr.success(`Tagged ${res.created + res.reactivated} EAN(s).`);
        // All-or-nothing save succeeded: drop the copy. Not closed, since the
        // grid resets for the next batch, which needs the same protection.
        this.draftStore?.clear();
        this.rows = [];
        this.addRows(INITIAL_ROWS);
        this.errorList = [];
        this.warningList = [];
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      error: (err) => {
        this.saving = false;
        this.handleHttpError(err, 'Save failed');
      },
    });
  }

  private handleHttpError(err: any, fallback: string): void {
    const body = err?.error ?? {};
    const errors: BarcodeRowError[] = Array.isArray(body.errors) ? body.errors : [];
    if (errors.length) this.applyServerErrors(errors, Array.isArray(body.warnings) ? body.warnings : []);
    const message =
      typeof body.message === 'string'
        ? body.message
        : Array.isArray(body.message)
          ? body.message.join('; ')
          : fallback;
    this.toastr.error(message, undefined, { timeOut: 8000 });
  }

  goToList(): void {
    this.router.navigate(['/product-list']);
  }

  async canDeactivate(): Promise<boolean> {
    if (this.submitted || this.filledRowCount === 0) return true;
    const leave = await this.confirmDialog.confirm({
      title: 'Leave page?',
      message: `You have <strong>${this.filledRowCount}</strong> unsaved row(s) on the Bulk EAN grid. Leave and discard them?`,
      confirmText: 'Leave',
      cancelText: 'Stay',
      size: 'lg',
    });
    // Leaving on purpose discards the browser copy too. Not when this is
    // the sign-out path (no token by now): that copy is the user's way back.
    if (leave && this.authService.isLoggedIn()) this.discardDraft();
    return leave;
  }

  // ── Unsaved-work safety copy ────────────────────────────────────────────
  // Prod 2026-10-29: a 140-line GRN was lost when the session ran out before
  // saving. This grid is just as long to key in, so it copies itself to the
  // browser every few seconds (FormDraftStore) and offers the copy back.
  private draftStore: FormDraftStore<BulkEanDraftSnapshot> | null = null;
  private draftTimer: ReturnType<typeof setInterval> | null = null;
  /** False until the restore question is settled, so the still-blank grid
   *  cannot overwrite the copy it is about to offer. */
  private draftReady = false;
  /** Discarded on purpose (Leave): stop copying. */
  private draftClosed = false;

  private initDraft(): void {
    this.draftStore = new FormDraftStore<BulkEanDraftSnapshot>([
      'bulk-ean',
      this.authService.getUserId(),
      this.authService.getActiveLocationId(),
      'new',
    ]);
    // Outside Angular: the timer must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.draftTimer = setInterval(() => this.autosaveDraft(), 5000);
    });
  }

  private draftRowCount(d: Partial<BulkEanDraftSnapshot> | null | undefined): number {
    return (d?.rows ?? []).filter((v) => !this.isBlankValues(v)).length;
  }

  private draftSnapshot(): BulkEanDraftSnapshot {
    return { rows: this.rows.map((r) => r.values) };
  }

  /** Also on pagehide: a closed tab or browser keeps the last few seconds. */
  @HostListener('window:pagehide')
  autosaveDraft(): void {
    if (!this.draftStore || !this.draftReady || this.draftClosed) return;
    const snapshot = this.draftSnapshot();
    // An emptied grid leaves any earlier copy alone ("Not now" kept it).
    if (this.draftRowCount(snapshot) > 0) this.draftStore.save(snapshot);
  }

  private discardDraft(): void {
    this.draftClosed = true;
    this.draftStore?.clear();
  }

  /** Offer a copy left by an earlier visit. */
  private async offerDraftRestore(): Promise<void> {
    const stored = this.draftStore?.load();
    const count = this.draftRowCount(stored?.data);
    if (!stored || !count) {
      this.draftReady = true;
      return;
    }

    const when = new Date(stored.savedAt).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const firstSku = stored.data.rows
      .map((v) => String(v?.['sku'] ?? '').trim())
      .find(Boolean);
    const details = [
      `<strong>${count}</strong> row${count === 1 ? '' : 's'}`,
      firstSku ? `starting with SKU <strong>${escapeHtml(firstSku)}</strong>` : '',
    ].filter(Boolean);
    const choice = await this.confirmDialog.choose({
      title: 'Restore unsaved EAN mapping?',
      message:
        `These barcodes were not saved. A copy from ${when} is still on this computer: ${details.join(', ')}.` +
        '<br><small class="text-muted">"Not now" keeps the copy, but it is replaced once you start entering barcodes here.</small>',
      confirmText: 'Restore',
      altText: 'Discard',
      cancelText: 'Not now',
      size: 'lg',
    });
    if (choice === 'confirm') this.applyDraft(stored.data);
    else if (choice === 'alt') this.draftStore!.clear();
    this.draftReady = true;
  }

  private applyDraft(d: BulkEanDraftSnapshot): void {
    this.rows = [];
    (d.rows ?? []).slice(0, MAX_ROWS).forEach((values) => {
      const row = this.newRow();
      row.values = { ...row.values, ...values };
      this.ensureSkuOption(row);
      this.rows.push(row);
    });
    if (!this.rows.length) this.addRows(INITIAL_ROWS);
    this.errorList = [];
    this.warningList = [];
    this.lastResult = null;
    this.submitted = false;
    // Names, existing EANs and not-found errors come back from the lookup.
    this.lookupQueue.next();
    this.cdr.detectChanges();
    this.toastr.success(
      `${this.filledRowCount} row(s) restored. Save to tag the barcodes.`,
      'EAN mapping restored',
    );
  }

  trackRow(_: number, row: GridRow): number {
    return row.uid;
  }

  trackCol(_: number, col: ColumnDef): string {
    return col.key;
  }
}
